import * as jose from 'jose';
import * as lodash from 'lodash';
import type { SessionSliceType } from './types';

// decode accesstoken and return entitlements
export default function formatEntitlements(accessToken: string) {
  const payloadContent = accessToken ? jose.decodeJwt(accessToken) : null;
  if (payloadContent && 'entitlements' in payloadContent) {
    return payloadContent['entitlements' as keyof typeof payloadContent];
  }
  return payloadContent;
}

// decode token and return XsrfToken
export function getXsrfTokenFromJwt(token: string): string {
  const payload = token.split('.')[1];
  const payloadContent = JSON.parse(window.atob(payload));
  return payloadContent.xsrfToken;
}

/** Angular user-plan / enrollment parity for `itps:*:tips_and_insights` → news. */
export function rawEntitlementsIncludeNewsArticles(
  entitlements: SessionSliceType['entitlements'] | unknown,
): boolean {
  if (!entitlements || typeof entitlements !== 'object') return false;

  const entry = Object.entries(entitlements as Record<string, unknown>).find(
    ([key]) => key.split(':')[1] === 'tips_and_insights',
  );
  if (!entry) return false;

  const [, value] = entry;
  if (lodash.isArray(value)) {
    return value.includes('news_articles');
  }
  if (lodash.isPlainObject(value)) {
    const nested = (value as Record<string, unknown>).news_articles;
    return nested === true || nested === 1;
  }
  if (lodash.isString(value)) {
    return value === 'news_articles';
  }
  return false;
}

// extract itps entitlements and return entitlement keys ( ex: itps: dark_web => dark_web)
export function formatUserFeatures(entitlements: unknown): string[] {
  const formattedUserFeatures: string[] = [];
  if (!entitlements) return [];
  Object.entries(entitlements as Record<string, unknown>).forEach(
    ([key, value]) => {
      if (lodash.isArray(value)) {
        formattedUserFeatures.push(...value);
      } else if (lodash.isBoolean(value)) {
        const feature = key.split(':')[1];
        formattedUserFeatures.push(feature);
      } else if (lodash.isString(value)) {
        formattedUserFeatures.push(value);
      } else if (lodash.isNumber(value)) {
        const splitArr = key.split(':');
        if (splitArr[0].toLowerCase() === 'itps') {
          formattedUserFeatures.push(splitArr[1]);
        } else {
          formattedUserFeatures.push(splitArr[0]);
        }
      } else if (lodash.isPlainObject(value)) {
        Object.entries(value as Record<string, unknown>).forEach(
          ([nestedKey, nestedVal]) => {
            if (nestedVal === true || nestedVal === 1) {
              formattedUserFeatures.push(nestedKey);
            }
          },
        );
      }
    },
  );

  if (rawEntitlementsIncludeNewsArticles(entitlements)) {
    if (!formattedUserFeatures.includes('news_articles')) {
      formattedUserFeatures.push('news_articles');
    }
    if (!formattedUserFeatures.includes('feat_insight_tips')) {
      formattedUserFeatures.push('feat_insight_tips');
    }
  }

  return formattedUserFeatures;
}

export function getAgentIdFromAccessToken(accessToken: string): string | null {
  if (!accessToken) return null;
  const payload = jose.decodeJwt(accessToken);
  return (payload?.agent_name as string) ?? null;
}

export function getUserIdFromAccessToken(
  accessToken: string,
): string | undefined {
  if (!accessToken) return undefined;
  const payload = jose.decodeJwt(accessToken);
  return payload.sub as string;
}

export function getSessionIdFromAccessToken(
  accessToken: string,
): string | undefined {
  if (!accessToken) return undefined;
  const payload = jose.decodeJwt(accessToken);
  return (payload?.session_id ?? '') as string;
}
